import { useRef, useEffect, useState } from "react";
import {
  IconButton,
  List,
  ListItem,
  ListItemText,
  Button,
  Box,
  Grid,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import {
  Controller,
  useFieldArray,
  useWatch,
  Control,
  FieldValues,
} from "react-hook-form";
import {
  Textarea,
  ConfirmationModal,
  Typography,
} from "@platform/design-system";

import { brand } from "../../../../theme/brand";
import { paletteVar } from "../../../../theme/palette-var";

import type { FieldComponent } from "../types";

interface RepeaterTextAreaItemProps {
  index: number;
  fieldName: string;
  control: Control<FieldValues>;
  onDelete: (index: number) => void;
  inputRef: (el: HTMLTextAreaElement | null) => void;
  canDelete: boolean;
}

const RepeaterTextAreaItem: React.FC<RepeaterTextAreaItemProps> = ({
  index,
  fieldName,
  control,
  onDelete,
  inputRef,
  canDelete,
}) => (
  <ListItem
    disableGutters
    alignItems="center"
    secondaryAction={
      canDelete && (
        <IconButton onClick={() => onDelete(index)} edge="end">
          <DeleteIcon />
        </IconButton>
      )
    }
  >
    <Typography variant="body-medium" mr={2}>
      {index + 1}
    </Typography>
    <ListItemText
      primary={
        <Controller
          control={control}
          name={`${fieldName}.${index}`}
          render={({ field: controllerField }) => (
            <Textarea
              {...controllerField}
              label={`Description ${index + 1}`}
              fullWidth
              maxLength={1000}
              inputRef={inputRef}
              autoComplete="off"
            />
          )}
        />
      }
    />
  </ListItem>
);

const RepeaterTextAreaField: FieldComponent<never> = ({ field, methods }) => {
  const { control } = methods;

  const { fields, append, remove } = useFieldArray({
    control,
    name: field.name,
  });

  const watchedValue: unknown = useWatch({ control, name: field.name });
  const watchedValues: readonly unknown[] = Array.isArray(watchedValue)
    ? watchedValue
    : [];

  const inputRefs = useRef<(HTMLTextAreaElement | null)[]>([]);

  const [deleteIndex, setDeleteIndex] = useState<number | null>(null);

  const minItems = field.minItems ?? 0;
  const maxItems = field.maxItems;

  const canAddMore = !maxItems || fields.length < maxItems;
  const canDelete = fields.length > minItems;

  useEffect(() => {
    const indexToFocus = watchedValues.length - 1;
    if (inputRefs.current[indexToFocus]) {
      const el = inputRefs.current[indexToFocus];
      setTimeout(() => {
        el?.focus({ preventScroll: true });
        el?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 0);
    }
  }, [watchedValues.length]);

  const handleDelete = () => {
    if (deleteIndex !== null) {
      remove(deleteIndex);
      setDeleteIndex(null);
    }
  };

  const handleAdd = () => {
    append("");
  };

  return (
    <Box>
      <Grid
        container
        sx={{
          backgroundColor: paletteVar("surfaces-neutral", "#f2f2f2"),
          marginTop: (theme) => theme.spacing(3),
          marginBottom: (theme) => theme.spacing(3),
          padding: (theme) => theme.spacing(1.5, 2),
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          ...field.sx,
        }}
      >
        <Typography variant={field.variant || "header-medium"} component="div">
          <strong>
            {`${field.label}s (${fields.length}${maxItems ? `/${maxItems}` : ""})`}
          </strong>
        </Typography>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={handleAdd}
          color="inherit"
          sx={{
            color: brand("primary"),
            borderColor: brand("primary"),
            "&:hover": {
              color: brand("primaryHover"),
              borderColor: brand("primaryHover"),
            },
          }}
          disabled={!canAddMore}
        >
          {`Add ${field.label}`}
        </Button>
      </Grid>

      <List dense>
        {fields.map((item, index) => (
          <RepeaterTextAreaItem
            key={item.id}
            index={index}
            fieldName={field.name}
            control={control}
            onDelete={setDeleteIndex}
            inputRef={(el) => (inputRefs.current[index] = el)}
            canDelete={canDelete}
          />
        ))}
      </List>

      <ConfirmationModal
        open={deleteIndex !== null}
        onCancel={() => setDeleteIndex(null)}
        onAccept={handleDelete}
        title={`Delete ${field.label}?`}
        description="This cannot be undone."
        confirmButtonText="Yes, Delete"
        cancelButtonText="Cancel"
      />
    </Box>
  );
};

export default RepeaterTextAreaField;
